
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { BookingList } from "./components/BookingList";
import { useBookingActions } from "./hooks/useBookingActions";
import { useToast } from "@/hooks/use-toast";

export const BookingManager = () => {
  const { handleBookingAction, processingId } = useBookingActions();
  const { toast } = useToast();

  const { data: bookings, isLoading, error } = useQuery({
    queryKey: ['pending-bookings'],
    queryFn: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Not authenticated");

        console.log('Fetching bookings for host:', user.id);

        const { data, error: fetchError } = await supabase
          .from('bookings')
          .select(`
            *,
            property:properties(title),
            guest:profiles!bookings_user_id_fkey(full_name, phone_number)
          `)
          .eq('status', 'pending')
          .eq('properties.host_id', user.id)
          .order('created_at', { ascending: false });

        if (fetchError) {
          console.error('Supabase error:', fetchError);
          throw fetchError;
        }

        console.log('Fetched bookings:', data);
        return data;
      } catch (err) {
        console.error('Error in booking query:', err);
        toast({
          title: "Error loading bookings",
          description: err.message,
          variant: "destructive",
        });
        throw err;
      }
    }
  });

  if (error) {
    console.error('Booking manager error:', error);
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-red-500">
            Error loading bookings: {error.message}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pending Bookings</CardTitle>
      </CardHeader>
      <CardContent>
        <BookingList
          bookings={bookings}
          onApprove={(id) => handleBookingAction(id, 'approve', bookings?.find(b => b.id === id))}
          onReject={(id) => handleBookingAction(id, 'reject', bookings?.find(b => b.id === id))}
          processingId={processingId}
        />
      </CardContent>
    </Card>
  );
};
